package pl.decks.personal.components.widgets.revealtext

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.dom.ElementRefScope
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.silk.components.text.SpanTextKind
import com.varabyte.kobweb.silk.style.CssStyleVariant
import kotlinx.coroutines.delay
import org.w3c.dom.HTMLSpanElement
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds

private data class TimelineEvent(
    val revealedCount: Int,
    val delay: Duration,
)

private typealias SentenceSequence = List<RevealSentence>

private fun SentenceSequence.text() : String = this.joinToString(" ") { it.text }

@Composable
fun AnimatedRevealText(
    sentences: SentenceSequence,
    modifier: Modifier = Modifier,
    variant: CssStyleVariant<SpanTextKind>? = null,
    ref: ElementRefScope<HTMLSpanElement>? = null,
    letterTypeDuration: Duration? = null,
    wordStopDuration: Duration? = null,
    sentenceStopDuration: Duration? = null,
    letterDeleteDuration: Duration? = letterTypeDuration,
    trailing: @Composable () -> Unit = {},
) {
    val sequences = remember(sentences) { createSentenceSequences(sentences) }
    val longestText = remember(sequences) { sequences.map { it.text() }.maxBy { it.length } }

    var coverText by remember(sequences) { mutableStateOf(sequences.first().text()) }
    var revealEvent by remember(sequences) { mutableStateOf(TimelineEvent(0, 0.milliseconds)) }

    LaunchedEffect(sequences) {
        sequences.forEach { sequence ->
            coverText = sequence.text()
            val timeline = sequenceTimeline(
                sentences = sequence,
                letterMs = letterTypeDuration,
                wordMs = wordStopDuration,
                sentenceMs = sentenceStopDuration,
                deleteMs = letterDeleteDuration,
                startingChar = revealEvent.revealedCount,
            )
            timeline.forEach { event ->
                revealEvent = event
                delay(event.delay)
            }
        }
    }

    RevealText(
        text = coverText,
        reserveText = longestText,
        revealedCount = revealEvent.revealedCount,
        modifier = modifier,
        variant = variant,
        ref = ref,
        trailing = trailing,
    )
}

private fun createSentenceSequences(initialSentences: SentenceSequence): List<SentenceSequence> {
    val sequences = mutableListOf<List<RevealSentence>>()
    val writtenOutSentences = mutableListOf<RevealSentence>()
    initialSentences.forEach { sent ->
        if (sent.keep) writtenOutSentences.add(sent)
        else sequences.add(writtenOutSentences + sent)
    }
    sequences.add(writtenOutSentences)
    return sequences
}

private fun sequenceTimeline(
    sentences: SentenceSequence,
    letterMs: Duration? = null,
    wordMs: Duration? = null,
    sentenceMs: Duration? = null,
    deleteMs: Duration? = null,
    startingChar: Int = 0,
): List<TimelineEvent> {
    var charIndex = 0
    val events = mutableListOf<TimelineEvent>()

    sentences.forEachIndexed { si, sentence ->
        val sentWords = sentence.wordTokens.mapIndexed { wi, word ->
            if (si != 0 && wi == 0) " $word" else word
        }
        sentWords.forEach { word ->
            word.forEach { _ ->
                charIndex++
                if (charIndex > startingChar && letterMs != null) events.add(TimelineEvent(charIndex, letterMs))
            }
            if (charIndex > startingChar && wordMs != null) events.add(TimelineEvent(charIndex, wordMs))
        }
        if (charIndex > startingChar && sentenceMs != null) events.add(TimelineEvent(charIndex, sentenceMs))

        if (!sentence.keep) {
            sentWords.forEach { word ->
                word.forEach {
                    charIndex--
                    if (deleteMs != null) events.add(TimelineEvent(charIndex, deleteMs))
                }
            }
            if (sentenceMs != null) events.add(TimelineEvent(charIndex, sentenceMs))
        }
    }
    events.add(TimelineEvent(charIndex, 0.milliseconds))

    return events
}
